/**
 * useCustomerDocumentLookup
 *
 * Consulta de cliente + veículos por CPF/CNPJ em sistemas externos (ERP),
 * 100% abstraída pelo Integration Gateway. O frontend NÃO conhece provider,
 * URL, credencial ou formato de resposta: informa apenas a conexão e o
 * documento, e recebe o objeto canônico { customer, vehicles }.
 *
 * Multi-provider: qualquer provider que declare a operação
 * `customer.lookup_document` aparece automaticamente como origem disponível
 * (a RPC list_integration_connections filtra por capacidade).
 *
 * RELEASE: 2026-08-23 v1.0.0 - Versão inicial (cadastro automático via SGCNET).
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

// =============================================================================
// TIPOS CANÔNICOS (independentes de provider)
// =============================================================================

/** Endereço normalizado retornado pelo gateway */
export interface LookupAddress {
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
}

/** Cliente normalizado retornado pelo gateway */
export interface LookupCustomer {
  external_id: string | null;
  name: string | null;
  document: string | null;
  document_digits: string;
  email: string | null;
  phone: string | null;
  address: LookupAddress;
}

/** Veículo normalizado retornado pelo gateway */
export interface LookupVehicle {
  external_key: string;
  vehicle_type: 'CAR' | 'MOTORCYCLE' | 'TRUCK' | 'OTHER';
  raw_type: string | null;
  brand_name: string | null;
  model_name: string | null;
  year: number | null;
  raw_year: string | null;
  fuel: string | null;
  color: string | null;
  plate: string | null;
  renavam: string | null;
  chassis: string | null;
  fipe_code: string | null;
  fipe_value: number | null;
  coverage_number: string | null;
  coverage_date: string | null;
  tracker_number: string | null;
  contract_type: string | null;
}

/** Resultado completo da consulta */
export interface LookupResult {
  found: boolean;
  message?: string;
  customer: LookupCustomer | null;
  vehicles: LookupVehicle[];
}

/** Conexão elegível para a consulta por documento */
export interface LookupConnection {
  id: string;
  name: string;
  provider_slug: string;
  provider_name: string;
  is_active: boolean;
  is_default: boolean;
}

/** Código da operação (capacidade) exigida do provider */
export const LOOKUP_OPERATION_CODE = 'customer.lookup_document';

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Lista as conexões ATIVAS do tenant capazes de consultar cliente por documento.
 * Usado para decidir se a opção "Automático" deve aparecer na UI.
 */
export function useDocumentLookupConnections(enabled = true) {
  const query = useQuery({
    queryKey: ['integration-connections', 'capability', LOOKUP_OPERATION_CODE],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<LookupConnection[]> => {
      const { data, error } = await (supabase.rpc as any)('list_integration_connections', {
        p_provider_slug: null,
        p_search: null,
        p_operation_code: LOOKUP_OPERATION_CODE,
      });
      if (error) {
        logger.error('CustomerDocumentLookup', 'Falha ao listar conexões', {
          message: error.message,
        });
        throw error;
      }
      return ((data ?? []) as LookupConnection[]).filter((c) => c.is_active);
    },
  });

  const connections = query.data ?? [];
  const defaultConnection = connections.find((c) => c.is_default) ?? connections[0] ?? null;

  return {
    connections,
    defaultConnection,
    /** Indica se o tenant pode usar o cadastro automático */
    isAvailable: connections.length > 0,
    isLoading: query.isLoading,
  };
}

/**
 * Executa a consulta por documento através do Integration Gateway.
 * Nunca expõe credenciais: apenas connection_id + documento saem do browser.
 */
export function useCustomerDocumentLookup() {
  return useMutation({
    mutationFn: async (input: {
      connectionId: string;
      document: string;
    }): Promise<LookupResult> => {
      const digits = (input.document ?? '').replace(/\D+/g, '');
      if (digits.length !== 11 && digits.length !== 14) {
        throw new Error('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido');
      }

      const { data, error } = await supabase.functions.invoke('integration-gateway', {
        body: {
          action: 'execute',
          connection_id: input.connectionId,
          operation: LOOKUP_OPERATION_CODE,
          params: { document: digits },
        },
      });

      if (error) {
        // FunctionsHttpError: tenta extrair a mensagem de negócio do corpo
        let message = error.message || 'Não foi possível consultar o sistema externo';
        try {
          const body = await (error as any)?.context?.json?.();
          if (body?.error) message = body.error as string;
        } catch {
          // corpo não-JSON: mantém a mensagem original
        }
        logger.error('CustomerDocumentLookup', 'Falha na consulta', { message });
        throw new Error(message);
      }

      // O gateway responde { success: true, data: <resultado do adapter> }
      const payload = (data as any)?.data ?? data;
      return payload as LookupResult;
    },
  });
}
